import { useState, useEffect, useMemo } from 'react';
import { createBusinessDocumentationHandlers } from '../handlers/businessDocumentationHandlers.js';

/**
 * Custom hook for Business Documentation
 * Manages all state internally - parent only needs to know completion status
 */
export const useBusinessDocumentation = ({
  onboardingData,
  setOnboardingData,
  updateStateFromAPI,
  loadOnboardingData
}) => {
  // All internal state - parent doesn't need to know about these
  const [businessUploadedFiles, setBusinessUploadedFiles] = useState([]);
  const [isBusinessUploading, setIsBusinessUploading] = useState(false);
  const [businessUploadProgress, setBusinessUploadProgress] = useState('');
  const [businessUploadStatus, setBusinessUploadStatus] = useState('');
  const [isViewingBusinessFile, setIsViewingBusinessFile] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Derived completion status from onboardingData
  const businessCompleted = useMemo(() => {
    return onboardingData?.stepData?.businessDocumentation?.completed || false;
  }, [onboardingData]);

  // Load files from onboarding data when it changes
  useEffect(() => {
    if (onboardingData?.stepData?.businessDocumentation?.files) {
      const mappedFiles = onboardingData.stepData.businessDocumentation.files.map((fileData, index) => ({
        id: fileData.s3Key || `business-${index + 1}`,
        name: typeof fileData === 'string' ? fileData : fileData.name,
        s3Key: typeof fileData === 'string' ? null : fileData.s3Key,
        size: typeof fileData === 'string' ? null : fileData.size,
        uploadDate: typeof fileData === 'string' ? new Date().toISOString() : fileData.uploadDate
      }));
      setBusinessUploadedFiles(mappedFiles);
    }
  }, [onboardingData]);

  // Create handlers using the handler factory
  const handlers = useMemo(() => {
    const handlerParams = {
      setBusinessUploadedFiles,
      setIsBusinessUploading,
      setBusinessUploadProgress,
      setBusinessUploadStatus,
      setIsViewingBusinessFile,
      setBusinessCompleted: () => {
        // Completion is derived from API data, so we just refresh
        // The handler will update the API, then we reload
        if (loadOnboardingData) {
          loadOnboardingData();
        }
      },
      setShowBusinessDocumentation: setShowModal,
      businessUploadedFiles,
      onboardingData,
      setOnboardingData,
      updateStateFromAPI,
      loadOnboardingData
    };
    return createBusinessDocumentationHandlers(handlerParams);
  }, [businessUploadedFiles, onboardingData, setOnboardingData, updateStateFromAPI, loadOnboardingData]);

  return {
    // Completion status (derived from API)
    businessCompleted,
    
    // Modal control
    showModal,
    setShowModal,
    
    // State for modal (internal but needed by modal component)
    businessUploadedFiles,
    isBusinessUploading,
    businessUploadProgress,
    businessUploadStatus,
    isViewingBusinessFile,
    
    // Handlers
    handleBusinessFileUpload: handlers.handleBusinessFileUpload,
    handleViewBusinessFile: handlers.handleViewBusinessFile,
    handleDeleteBusinessFile: handlers.handleDeleteBusinessFile,
    handleCompleteBusinessDocumentation: handlers.handleCompleteBusinessDocumentation
  };
};

